/**
 * appConfig.js — Re Leve IA (Etapa 7)
 * Configuração central de modo, ambiente e serviços externos.
 *
 * Lê variáveis VITE_ do ambiente (definidas em .env ou no servidor de CI/CD).
 * Se a variável não existir, usa fallback seguro — sempre modo demo.
 *
 * REGRA DE OURO:
 *   - O app NUNCA quebra por variável ausente.
 *   - O padrão é sempre o modo mais seguro (demo + motores locais).
 *   - Chaves secretas (OpenAI, DB) jamais entram aqui — ficam no backend.
 */

function readEnv(key, fallback) {
  try {
    const val = import.meta.env[key];
    return val !== undefined && val !== '' ? val : fallback;
  } catch {
    return fallback;
  }
}

function readBool(key, fallback = false) {
  const val = readEnv(key, String(fallback));
  return val === 'true' || val === '1';
}

// ── Modo de operação ──────────────────────────────────────────────────────────
const mode = readEnv('VITE_APP_MODE', 'demo');
const isDemoMode = mode !== 'real';
const isRealMode = mode === 'real';

// ── Roteamento ────────────────────────────────────────────────────────────────
const basePath = readEnv('VITE_APP_BASE_PATH', '/releve/');

// ── Serviços externos (todos opcionais) ───────────────────────────────────────
const enableRealApi = readBool('VITE_ENABLE_REAL_API', false);
const apiBaseUrl = readEnv('VITE_API_BASE_URL', '');

const enableLLM = readBool('VITE_ENABLE_LLM', false);
const enableN8N = readBool('VITE_ENABLE_N8N', false);

const enableRealWhatsApp = readBool('VITE_ENABLE_REAL_WHATSAPP', false);
// Número da Rê Leve no formato internacional sem +, ex: 5581999998888 (55=BR, 81=PE)
// Quando vazio, os links de WhatsApp viram placeholder com modal de aviso.
const whatsappNumber = readEnv('VITE_WHATSAPP_NUMBER', '');
const whatsappDefaultMessage = readEnv(
  'VITE_WHATSAPP_DEFAULT_MESSAGE',
  'Oi! Vi o site da Rê Leve e quero montar minha semana. 🌱'
);

// ── Motor de dados ────────────────────────────────────────────────────────────
// Se API real não está ativa, os motores locais (JSON + localStorage) são usados.
const useLocalEngines = !enableRealApi || isDemoMode;
// Se LLM não está ativa, o motor determinístico responde.
const useDeterministicBot = !enableLLM || isDemoMode;
// Se n8n não está ativo, eventos são registrados apenas localmente.
const useLocalEvents = !enableN8N || isDemoMode;

// ── Target de deploy ──────────────────────────────────────────────────────────
const deploymentTarget = 'mrs.app.br/releve';

// ── WhatsApp link builder ─────────────────────────────────────────────────────
// Quando whatsappNumber está configurado, gera link wa.me real.
// Quando vazio, retorna sentinela "wa-placeholder:" que os componentes usam
// para abrir um modal explicando "WhatsApp ainda não configurado".
const hasWhatsAppNumber = Boolean(whatsappNumber && whatsappNumber.replace(/\D/g, '').length >= 10);

function buildWhatsAppLink(customMessage) {
  const msg = encodeURIComponent(customMessage || whatsappDefaultMessage);
  if (hasWhatsAppNumber) {
    const clean = whatsappNumber.replace(/\D/g, '');
    return `https://wa.me/${clean}?text=${msg}`;
  }
  return 'wa-placeholder:' + msg; // Sentinela — componentes detectam e mostram aviso
}

// Helper para anchors: retorna { href, onClick } prontos para uso.
function whatsAppAnchorProps(customMessage) {
  const href = buildWhatsAppLink(customMessage);
  if (href.startsWith('wa-placeholder:')) {
    return {
      href: '#whatsapp-pendente',
      onClick: (e) => {
        e.preventDefault();
        const msg = 'WhatsApp da Rê Leve ainda não está configurado.\n\n' +
          'Quando o número estiver pronto, a configuração é trocar VITE_WHATSAPP_NUMBER no Render.\n\n' +
          'Mensagem que seria enviada:\n"' + decodeURIComponent(href.replace('wa-placeholder:', '')) + '"';
        if (typeof window !== 'undefined') window.alert(msg);
      },
      'data-whatsapp-pending': 'true',
      target: '_self',
      rel: 'noopener noreferrer',
    };
  }
  return {
    href,
    target: '_blank',
    rel: 'noopener noreferrer',
  };
}

const appConfig = {
  mode,
  isDemoMode,
  isRealMode,
  basePath,
  apiBaseUrl,
  enableRealApi,
  enableLLM,
  enableN8N,
  enableRealWhatsApp,
  whatsappNumber,
  whatsappDefaultMessage,
  hasWhatsAppNumber,
  deploymentTarget,
  useLocalEngines,
  useDeterministicBot,
  useLocalEvents,
  buildWhatsAppLink,
  whatsAppAnchorProps,
};

export default appConfig;
